import { get, post } from "@/shared/utils/fetchApi";

export type LocalizedString = string | { [lang: string]: string };
export type LocalizedStringArray = string[] | { [lang: string]: string[] };

export interface FeedbackFormField {
  key: string;
  label: LocalizedString;
  type: "text" | "textarea" | "select" | "rating";
  required?: boolean;
  options?: LocalizedStringArray;
  placeholder?: LocalizedString;
  description?: LocalizedString;
}

export interface FeedbackConfig {
  enabled: boolean;
  session_timeout_minutes: number;
  session_count_threshold: number;
  ga4_enabled?: boolean;
  ga4_event_name?: string;
  title?: {
    pt?: string;
    en?: string;
  };
  form_fields: FeedbackFormField[];
  already_submitted?: boolean;
  /**
   * Cumulative number of `user_session` events for this user (server-counted).
   * Used by the hook to evaluate the `session_count_threshold` trigger.
   */
  login_count: number;
}

interface FeedbackConfigApiConfig {
  fields: {
    title?: Record<string, string>;
    form_fields: FeedbackFormField[];
  };
  value?: {
    enabled?: boolean;
    session_timeout_minutes: number;
    session_count_threshold?: number;
    ga4_enabled?: boolean;
    ga4_event_name?: string;
  };
}

interface FeedbackConfigApiResponse {
  config: FeedbackConfigApiConfig;
  status: {
    hasFeedback: boolean;
    submittedAt?: string;
    loginCount?: number;
  };
}

export interface FeedbackConfigResponse {
  success: boolean;
  data?: FeedbackConfigApiResponse;
}

export interface SubmitFeedbackPayload {
  answers: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface SubmitFeedbackResponse {
  success: boolean;
  data?: {
    /** Opcional porque a rota pode não mandar; ninguém lê hoje. */
    submittedAt?: string;
  };
}

export async function fetchFeedbackConfig(): Promise<FeedbackConfig | null> {
  try {
    // GET /feedback/config retorna FeedbackConfigApiResponse raw OU 204 (sem body).
    const raw = await get<FeedbackConfigApiResponse | undefined>(
      "/feedback/config",
    );
    if (!raw) return null; // 204 ⇒ fetchApi devolve undefined
    const apiConfig = raw.config;
    const apiStatus = raw.status;

    if (!apiConfig || !apiStatus) return null;

    const mapped: FeedbackConfig = {
      // Defaults preserve backward-compat with rows persisted before these
      // fields existed: `enabled` is implicitly true, `session_count_threshold`
      // is implicitly 0 (disabled — only the timeout trigger fires).
      enabled: apiConfig.value?.enabled ?? true,
      session_timeout_minutes: apiConfig.value?.session_timeout_minutes ?? 0,
      session_count_threshold: apiConfig.value?.session_count_threshold ?? 0,
      ga4_enabled: apiConfig.value?.ga4_enabled,
      ga4_event_name: apiConfig.value?.ga4_event_name,
      title: apiConfig.fields.title,
      form_fields: apiConfig.fields.form_fields || [],
      already_submitted: apiStatus.hasFeedback,
      login_count: apiStatus.loginCount ?? 0,
    };

    // Skip only when the feature is globally disabled or when there are no
    // fields to render. We intentionally DO NOT bail when both auto-triggers
    // are off: the config must still be available so the manual deeplink
    // (e.g. the "Send Feedback" email button -> ?feedback=1) can open the
    // form. The "no auto-popup when no trigger" behaviour is enforced by the
    // per-trigger guards inside useFeedbackPrompt.schedulePrompt.
    if (!mapped.enabled || mapped.form_fields.length === 0) {
      return null;
    }

    return mapped;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // 204 ou ausência de configuração não devem quebrar o app
    const status = error?.status ?? error?.response?.status;
    if (status === 204) {
      return null;
    }

    return null;
  }
}

export async function submitFeedback(
  payload: SubmitFeedbackPayload,
): Promise<SubmitFeedbackResponse | null> {
  try {
    // v2 responde flat {submittedAt} — re-embrulha no shape legacy que o
    // caller consome ({success, data:{submittedAt}}).

    const raw = await post<{ submittedAt?: string }>("/feedback", payload);
    // Repassa o que veio em vez de inventar string vazia. Não falha por falta do
    // campo: o 2xx é o que diz que o feedback foi gravado, e recusar aqui faria o
    // prompt voltar para quem já respondeu.
    return { success: true, data: { submittedAt: raw?.submittedAt } };
  } catch {
    return null;
  }
}
